import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { useAuth } from '../contexts/AuthContext';
import { useSecurity } from '../hooks/useSecurity';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GraduationCap, Eye, EyeOff, Loader2, UserCircle, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { FloatingLanguageSelector } from '../components/LanguageSelector';
import LegalAcceptance from '../components/LegalAcceptance';
import { formatDateLong } from '../lib/dateFormat';
import { redirectToGoogleLogin } from '../lib/googleAuth';
import { redirectToAppleLogin } from '../lib/appleAuth';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { useNativeSocialLogin } from '../hooks/useNativeSocialLogin';
import { useSoltarAlRegresar } from '../hooks/useSoltarAlRegresar';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
// LAPIDA post-t7-2c (E1): murio la franja de "vienes invitado por" (el programa de invitaciones cerro).
import { useConnectingRetry } from '../hooks/useConnectingRetry';
import { AUTH_FAILURE, classifyAuthFailure } from '../lib/authFailure';
// F6.8 T5-6 — `ensureNetworkForAuthAction` es el portero de auth, en una sola
// copia para las cuatro puertas de estas dos pantallas (el racional largo vive
// en su docblock, en networkVerdict.js).
import { isExplicitlyOffline, ensureNetworkForAuthAction } from '../lib/offline/networkVerdict';
import { logNetError } from '../lib/diag/netError';
import { usePageTitle } from '../hooks/usePageTitle';
import { destinoDespuesDeEntrar } from '../lib/rutaPendiente';
import { avisoDelFreno } from '../lib/avisoDelFreno';
import { vieneDelGuardia } from '../lib/razonDelRegistro';

/* pantallas-alumno (G), P12 de la parte 2: el estado de la navegacion con el que llego el invitado (Ajustes y
   "Earn free credits" lo rebotan aqui con su razon). La llamada va SIEMPRE y en el mismo orden; sin router (o con un
   doble de react-router-dom que no trae `useLocation`, como varias pruebas viejas) no hay estado y el registro se ve
   como hoy. Mismo patron que `useRutaActual` de `components/routeGuards.js`. */
function useEstadoDeLaNavegacion() {
  let location = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    location = useLocation();
  } catch {
    location = null;
  }
  return location ? location.state : null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// post-t7-2c (D6): los codigos del 429 de "demasiados intentos" con que el servidor manda a empezar de nuevo
// (`backend/routes/auth.py`: `/auth/verify` y `/auth/verify-guest-conversion`).
const CODIGOS_DE_VOLVER_AL_FORMULARIO = ['auth_too_many_attempts_signup_again', 'auth_too_many_attempts_start_over'];

// post-t7-2b (#615): `codigoNoSalio` = el servidor dijo `email_sent: false` y NO mando el
// codigo (con la llave de Resend puesta el codigo ya nunca viaja). En vez de "Enviamos un
// codigo de 6 digitos a ..." se dice el texto ratificado y el boton de reenviar sigue.
function VerificationStep({ email, expectedCode, codigoNoSalio, loading, onVerify, onBack, onResend, onFailLimit, setVerificationCode }) {
  const { t } = useReadyTranslation(['auth', 'common']);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  const codeString = code.join('');

  useEffect(() => {
    setVerificationCode(codeString);
  }, [codeString, setVerificationCode]);

  const focusInput = (i) => { inputRefs.current[i]?.focus(); };

  const handleChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return;
    const next = [...code];
    next[index] = digits[digits.length - 1];
    setCode(next);
    if (index < 5) focusInput(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        const next = [...code];
        next[index - 1] = '';
        setCode(next);
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < 5) {
      focusInput(index + 1);
    } else if (e.key === 'Enter' && code.every(d => d !== '')) {
      e.preventDefault();
      onVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!digits.length) return;
    const next = [...code];
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    setCode(next);
    focusInput(Math.min(digits.length, 5));
  };

  const handleResend = async () => {
    setResending(true);
    try { await onResend(); } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0e0e0e]">
      <div className="w-full max-w-md space-y-8">
        <button onClick={onBack} className="flex items-center gap-2 text-[#6a6a6a] hover:text-white transition h-11 text-sm" data-testid="verify-back-btn">
          <ArrowLeft className="w-4 h-4" /> {t('auth:signup.verify.back_btn')}
        </button>
        <div className="text-center">
          <div className="w-16 h-16 bg-[#FF6B00]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#FF6B00]" />
          </div>
          <h1 className="text-3xl font-bold text-white" data-testid="verify-title">{t('auth:signup.verify.title')}</h1>
          {codigoNoSalio
            ? <p className="text-[#6a6a6a] mt-2 text-sm" data-testid="verify-subtitle">{t('common:prod.codigo.nosalio')}</p>
            : <p className="text-[#6a6a6a] mt-2 text-sm" data-testid="verify-subtitle">{t('auth:signup.verify.subtitle_prefix')} <span className="text-white font-medium">{email}</span></p>}
        </div>

        <div className="glass-card rounded-[20px] p-8 space-y-6" data-testid="verify-form">
          {expectedCode && (
            <div className="p-4 rounded-xl bg-[rgba(255,107,0,0.08)] border border-[rgba(255,107,0,0.15)] text-center">
              <p className="text-xs text-[#6a6a6a] mb-1">{t('auth:signup.verify.dev_mode_label')}</p>
              <p className="text-2xl font-mono font-bold text-[#FF6B00]" data-testid="dev-code">{expectedCode}</p>
            </div>
          )}

          <div className="flex justify-center gap-2">
            {code.map((digit, i) => (
              <input key={i} ref={el => inputRefs.current[i] = el}
                type="text" inputMode="numeric" pattern="[0-9]*"
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                className="w-12 h-14 text-center text-2xl font-bold bg-[#0e0e0e] border-2 border-[#262626] rounded-xl text-white focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 transition-colors outline-none"
                data-testid={`verify-input-${i}`}
              />
            ))}
          </div>

          <Button onClick={onVerify} disabled={loading || codeString.length !== 6}
            className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white glow-primary rounded-xl h-12 text-base font-semibold"
            data-testid="verify-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth:signup.verify.submit_btn')}
          </Button>

          <div className="text-center">
            <p className="text-sm text-[#6a6a6a] mb-3">{t('auth:signup.verify.didnt_receive')}</p>
            <button type="button" onClick={handleResend} disabled={resending}
              className="text-[#FF6B00] hover:text-[#ff8c42] text-sm font-medium inline-flex items-center gap-2 transition h-11"
              data-testid="resend-btn">
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t('auth:signup.verify.resend_btn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function SignupPage() {
  const { t, ready, LoadingGate } = useReadyTranslation(['auth', 'common']);
  // BLOG-1-CIERRE (S6): la pestana en el idioma del usuario. Ver `hooks/usePageTitle.js`.
  usePageTitle(t('auth:signup.meta_title'));
  // pantallas-alumno (G), P12: el invitado llego porque quiso abrir una pagina que pide cuenta
  const razonDelGuardia = vieneDelGuardia(useEstadoDeLaNavegacion());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [expectedCode, setExpectedCode] = useState('');
  // post-t7-2b (#615): el correo con el codigo no salio y el codigo no vino (ver VerificationStep).
  const [codigoNoSalio, setCodigoNoSalio] = useState(false);
  const { signup, loginAsGuest, verifyEmail, resendVerification, logout, user,
          convertGuest, verifyGuestConversion, resendGuestConversion, cancelGuestConversion } = useAuth();
  const { getTurnstileToken, getFingerprint, turnstileRef } = useSecurity();

  // F6.7b (2ª pasada) — el DICTAMEN del vigía (ver LoginPage): con `offline`
  // recibido el formulario avisa al instante en vez de colgarse hasta el techo
  // de espera, y se destraba SOLO al volver la señal. Inerte en web.
  const net = useNetworkStatus();
  // F6.7b (pasada final 2) — el submit que pelea solo, gemelo del de LoginPage:
  // forma-de-red sin dictamen offline → "Conectando…" + escalera 1s→2s→4s→5s,
  // con Cancelar. Credenciales/4xx con significado → error inmediato, cero
  // reintentos. Dictamen offline → el aviso actual.
  const { connecting, start: startConnecting, cancel: cancelConnecting } = useConnectingRetry();
  const navigate = useNavigate();
  // AS-5 — los dos logins nativos (app-only). Gemelo del de LoginPage: en web el hook se
  // va en la primera línea de su handler y el chunk nativo jamás se pide.
  // post-t7-1 (E1): quien entra llega a donde iba (la ruta que guardo el guardia) o al Panel
  const irAlPanel = useCallback(() => { navigate(destinoDespuesDeEntrar()); }, [navigate]);
  const { pendiente: pendienteSocial, entrarConApple, entrarConGoogle, soltar: soltarSocial } = useNativeSocialLogin({ onSuccess: irAlPanel });

  // TANDA 24 (bloque D, firma #226) — gemelo del de `LoginPage`, y a propósito: son
  // el mismo par de botones en la otra superficie de entrada, y tener dos memorias
  // musculares distintas del mismo producto es justo lo que AS-5 vino a evitar.
  // La razón entera —por qué un handler síncrono necesita estado de carga— está
  // escrita junto a su gemelo, en `LoginPage.js`.
  const [pendienteWeb, setPendienteWeb] = useState(null);
  const irAProveedorWeb = useCallback((proveedor) => {
    setPendienteWeb(proveedor);
    if (proveedor === 'apple') redirectToAppleLogin();
    else redirectToGoogleLogin();
  }, []);
  // estilos-1 (Bloque E, apunte 1): el regreso a esta pantalla (pageshow con persisted, popstate, o la pestaña o la
  // app que vuelve a estar visible) apaga las dos ruedas, la de la web y la nativa. Gemelo del de `LoginPage`.
  useSoltarAlRegresar(useCallback(() => {
    setPendienteWeb(null);
    if (soltarSocial) soltarSocial();
  }, [soltarSocial]));

  // El dictamen offline corta el pleito: el aviso actual toma el relevo.
  useEffect(() => {
    if (net.isOffline) cancelConnecting();
  }, [net.isOffline, cancelConnecting]);

  // Cambio de identidad (nombre/correo/contraseña) → el pleito muere.
  useEffect(() => { cancelConnecting(); }, [name, email, password, cancelConnecting]);
  // A guest reaching /signup must convert IN-PLACE (same path as the Dashboard modal:
  // /auth/convert-guest + /auth/verify-guest-conversion) so their already-processed
  // documents are preserved. Non-guests keep the normal /auth/signup + /auth/verify flow.
  const isGuest = !!user?.is_guest;
  const [blockedUntil, setBlockedUntil] = useState(null);  // candado 30 dias post-borrado

  // El error de un intento fallido — mismo orden que LoginPage: detalle
  // específico del backend → los cubos que antes caían al mensaje mudo →
  // mensaje propio del servidor. Compartido por el submit y los reintentos.
  const showSignupFailure = useCallback((err) => {
    const detail = err.response?.data?.detail;
    const kind = classifyAuthFailure(err);
    if (detail?.code === 'email_recently_deleted') {
      setBlockedUntil(detail.blocked_until);  // banner inline (candado 30 dias)
    } else if (kind === AUTH_FAILURE.OFFLINE) {
      toast.error(t('auth:signup.toasts.no_connection'));
    } else if (kind === AUTH_FAILURE.SERVER) {
      toast.error(t('auth:signup.toasts.server_error'));
    } else {
      // post-t7-2b remate (R2, 5.3): el 429 del freno, en el idioma del usuario.
      toast.error(avisoDelFreno(err, t) || (typeof detail === 'string' && detail) || t('auth:signup.toasts.signup_failed_fallback'));
    }
  }, [t]);

  const doSignup = useCallback(async () => {
    if (loading || connecting) return;
    if (!name || !email || !password) { toast.error(t('auth:signup.toasts.fill_fields')); return; }
    if (!EMAIL_REGEX.test(email.trim())) { toast.error(t('auth:signup.toasts.invalid_email')); return; }
    // pantallas-alumno (G), firma 3 de la parte 2: minimo 8 (el servidor tambien; las cuentas de 6 o 7 siguen entrando)
    if (password.length < 8) { toast.error(t('auth:signup.toasts.password_too_short')); return; }
    // Sin señal DICTAMINADA: aviso ahora, no dentro de 10 s.
    //
    // F6.8 T5-5(b) — segunda opinión antes de rechazar, la MISMA pieza que usa
    // LoginPage: UNA consulta fresca al sensor nativo, techo de 5 s, cero
    // polling. "Conectado" corrige el veredicto a UP y deja pasar el submit;
    // cualquier otra cosa rechaza con el toast de hoy. (`!== true`: el atajo
    // síncrono del portero — ver su docblock en networkVerdict.js.)
    const gate = ensureNetworkForAuthAction(net.isOffline);
    if (gate !== true && !(await gate)) {
      toast.error(t('auth:signup.toasts.no_connection'));
      return;
    }
    setLoading(true);
    // El intento, compartido por el submit y por cada peldaño del pleito (ver
    // LoginPage). 'retry' SOLO con fallo sin respuesta y sin dictamen offline;
    // cualquier 4xx con significado pinta su mensaje y termina.
    const attempt = async () => {
      try {
        let data;
        if (isGuest) {
          // In-place guest conversion. Send the device fingerprint exactly like the
          // upload/modal flow so convert-guest's device check passes. No Turnstile
          // (convert-guest doesn't require it, same as the Dashboard modal).
          const fp = await getFingerprint();
          data = await convertGuest({ name, email, password, fingerprint: fp || '' });
        } else {
          const turnstileToken = await getTurnstileToken();
          data = await signup(email, password, name, turnstileToken);
        }
        setExpectedCode(data.verification_code || '');
        // post-t7-2b (#615): sin codigo y sin correo, la pantalla lo dice (y no se
        // celebra con un aviso verde un correo que no salio).
        const noSalio = data.email_sent === false && !data.verification_code;
        setCodigoNoSalio(noSalio);
        setVerificationStep(true);
        if (data.email_sent) {
          toast.success(t('auth:signup.toasts.check_email'));
        } else if (!noSalio) {
          toast.success(t('auth:signup.toasts.please_verify'));
        }
        return 'done';
      } catch (err) {
        const kind = classifyAuthFailure(err);
        // Microscopio (pasada final 2): QUÉ forma tuvo el fallo, al visor.
        if (kind === AUTH_FAILURE.OFFLINE || kind === AUTH_FAILURE.SERVER) logNetError('signup', err);
        if (kind === AUTH_FAILURE.OFFLINE && !isExplicitlyOffline()) return 'retry';
        showSignupFailure(err);
        return 'stop';
      }
    };
    try {
      const outcome = await attempt();
      if (outcome === 'retry') startConnecting(attempt);
    } finally {
      setLoading(false);
    }
  }, [name, email, password, loading, connecting, isGuest, signup, convertGuest, getFingerprint, getTurnstileToken, t, net.isOffline, showSignupFailure, startConnecting]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doSignup();
  };

  const doVerify = useCallback(async () => {
    if (loading) return;
    if (!verificationCode) { toast.error(t('auth:signup.toasts.enter_code')); return; }
    setLoading(true);
    try {
      if (isGuest) {
        await verifyGuestConversion(verificationCode);
      } else {
        await verifyEmail(email, verificationCode);
      }
      toast.success(t('auth:signup.toasts.verify_success'));
      navigate(destinoDespuesDeEntrar());
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || t('auth:signup.toasts.invalid_code_fallback');
      // post-t7-2b remate (R2, 5.3): el aviso del 429 del freno va en el idioma del usuario.
      toast.error(avisoDelFreno(err, t) || detail);
      // post-t7-2c (D6): volver al formulario lo decide el `error_code` del 429 que el servidor ya manda
      // (`/auth/verify` y `/auth/verify-guest-conversion`: `CODIGOS_DE_VOLVER_AL_FORMULARIO`); el `detail` ya
      // viene traducido y en espanol no decia "too many". Sin codigo, el texto como respaldo, como antes.
      const codigo = err.response?.data?.error_code;
      const volver = codigo
        ? CODIGOS_DE_VOLVER_AL_FORMULARIO.includes(codigo)
        : typeof detail === 'string' && detail.toLowerCase().includes('too many');
      if (status === 429 && volver) {
        setVerificationStep(false);
        setEmail(''); setPassword(''); setName(''); setVerificationCode('');
      }
    } finally {
      setLoading(false);
    }
  }, [email, verificationCode, loading, isGuest, verifyEmail, verifyGuestConversion, navigate, t]);

  // Global Enter key listener — triggers signup or verify based on current step
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (verificationStep) {
          doVerify();
        } else {
          doSignup();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [verificationStep, doSignup, doVerify]);

  // Block render until i18n namespaces are loaded — prevents literal key flash on cold load
  if (!ready) return <LoadingGate />;

  const handleGuest = async () => {
    if (user) { navigate('/dashboard'); return; }
    // F6.8 T5-6 (C-16) — la MISMA segunda opinión que el submit, gemela de la de
    // LoginPage: entrar como invitado también es una puerta, no una lectura.
    // (`!== true`: el atajo síncrono.)
    const gate = ensureNetworkForAuthAction(net.isOffline);
    if (gate !== true && !(await gate)) {
      toast.error(t('auth:signup.toasts.no_connection'));
      return;
    }
    setLoading(true);
    try {
      await loginAsGuest();
      toast.success(t('common:toasts.guest_welcome'));
      navigate('/dashboard');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 429) {
        toast.error(t('common:toasts.guest_rate_limit'));
      } else {
        toast.error(typeof detail === 'string' && detail ? detail : t('common:toasts.guest_session_failed'));
      }
    } finally { setLoading(false); }
  };

  if (verificationStep) {
    const handleBack = async () => {
      if (isGuest) {
        // In-place conversion: just clear the pending_* fields on the guest doc.
        // DO NOT logout (that would end the guest session and strand their docs) and
        // DO NOT hit cancel-signup (no pending_verifications record exists here).
        try {
          await cancelGuestConversion();
        } catch (err) {
          console.warn('Cancel guest conversion failed (non-blocking):', err);
        }
      } else {
        try {
          await api.post('/auth/cancel-signup', { email });
        } catch (err) {
          console.warn('Cancel signup failed (non-blocking):', err);
        }
        try {
          await logout();
        } catch (err) {
          console.warn('Logout failed (non-blocking):', err);
        }
      }
      setEmail('');
      setPassword('');
      setName('');
      setVerificationCode('');
      setVerificationStep(false);
    };

    return <VerificationStep
      email={email}
      expectedCode={expectedCode}
      codigoNoSalio={codigoNoSalio}
      loading={loading}
      onVerify={doVerify}
      onBack={handleBack}
      onResend={async () => {
        try {
          const res = isGuest ? await resendGuestConversion() : await resendVerification(email);
          // post-t7-2b (#615): un reenvio que tampoco salio (y sin codigo) no dice "reenviado".
          // Una respuesta sin `email_sent` (un servidor viejo) sigue como antes.
          if (res && res.email_sent === false && !res.code) {
            setCodigoNoSalio(true);
            toast.error(t('common:prod.codigo.nosalio'));
            return;
          }
          setCodigoNoSalio(false);
          toast.success(t('auth:signup.toasts.code_resent'));
        } catch (err) {
          toast.error(avisoDelFreno(err, t) || err.response?.data?.detail || t('auth:signup.toasts.resend_failed_fallback'));
        }
      }}
      onFailLimit={() => {
        setVerificationStep(false);
        setEmail(''); setPassword(''); setName(''); setVerificationCode('');
      }}
      setVerificationCode={setVerificationCode}
    />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-4 bg-[#0e0e0e]" data-testid="signup-page">
      <FloatingLanguageSelector />
      <div className="w-full max-w-md space-y-4">
        <div className="text-center">
          <Link to="/" className="inline-block mb-3 font-['Space_Grotesk'] font-bold text-xl tracking-tight">
            Quiz<span className="text-[#FF6B00]">essa</span>
          </Link>
          <h1 className="text-2xl font-bold text-white" data-testid="signup-title">{t('auth:signup.title')}</h1>
          <p className="text-[#6a6a6a] mt-1 text-sm">{t('auth:signup.subtitle')}</p>
        </div>

        {blockedUntil && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-400" data-testid="deleted-block-banner">
            {t('auth:blocked.recently_deleted', { date: formatDateLong(blockedUntil) })}
          </div>
        )}

        {/* Aviso de red colgado del veredicto del vigía: aparece al perder la
            señal y desaparece SOLO al recuperarla (ver LoginPage). */}
        {net.isOffline && (
          <div className="bg-[#181818] border border-[#262626] rounded-xl p-3 text-sm text-[#a0a0a0]" data-testid="signup-offline-notice">
            {t('auth:signup.toasts.no_connection_banner')}
          </div>
        )}

        {/* pantallas-alumno (G), P12 de la parte 2: arriba del formulario, solo si el invitado viene del guardia. */}
        {razonDelGuardia && !connecting && (
          <div className="rounded-xl border border-[#3A2414] bg-[#1A120C] px-3.5 py-3 text-[14px] leading-snug text-[#F4F4F3]"
            role="status" data-testid="signup-guest-reason">
            {t('auth:signup.signup_guest_reason')}
          </div>
        )}

        {/* F6.7b (pasada final 2) — el pleito visible (ver LoginPage): mientras
            la escalera reintenta, la tarjeta entera es "Conectando…" + Cancelar
            y el formulario no se puede editar (identidad congelada). */}
        {connecting ? (
          <div className="glass-card rounded-[20px] p-6 flex flex-col items-center gap-4 py-8" data-testid="signup-connecting">
            <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
            <p className="text-sm text-[#a0a0a0]" data-testid="signup-connecting-msg">{t('auth:connecting')}</p>
            <Button type="button" variant="outline" onClick={cancelConnecting}
              className="border-[#262626] text-[#a0a0a0] hover:text-white hover:bg-[#181818] rounded-xl h-11 px-6"
              data-testid="signup-connect-cancel-btn">
              {t('common:cancel')}
            </Button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="glass-card rounded-[20px] p-6 space-y-4" data-testid="signup-form">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#a0a0a0] text-sm">{t('auth:signup.form.name_label')}</Label>
            <Input
              id="name" type="text" name="name" autoComplete="name" placeholder={t('auth:signup.form.name_placeholder')}
              value={name} onChange={(e) => setName(e.target.value)}
              className="bg-[#0e0e0e] border-[#262626] focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 rounded-xl h-12"
              data-testid="signup-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#a0a0a0] text-sm">{t('auth:signup.form.email_label')}</Label>
            <Input
              id="email" type="text" inputMode="email" name="email" autoComplete="username" placeholder={t('auth:signup.form.email_placeholder')}
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0e0e0e] border-[#262626] focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 rounded-xl h-12"
              data-testid="signup-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#a0a0a0] text-sm">{t('auth:signup.form.password_label')}</Label>
            <div className="relative">
              <Input
                id="password" type={showPw ? 'text' : 'password'} name="new_password" autoComplete="new-password" placeholder={t('auth:signup.form.password_placeholder')}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0e0e0e] border-[#262626] focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 rounded-xl h-12 pr-10"
                data-testid="signup-password-input"
              />
              <button type="button" tabIndex={-1} onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a6a6a] hover:text-[#a0a0a0]"
                data-testid="signup-toggle-password">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {/* F6.8 T5-6 — gemelo del de LoginPage: el botón ya no se ata al
              veredicto (C-12). Con un DOWN mentiroso el dedo llega al portero
              igual que la tecla Enter, y es el portero quien decide. El aviso
              visual es el banner de arriba, y SOLO ese: el `title` que repetía
              su texto se fue en la T5c (C-21), gemelo del de LoginPage. */}
          <Button type="submit" disabled={loading}
            className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white glow-primary rounded-xl h-12 text-base font-medium"
            data-testid="signup-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth:signup.form.submit_btn')}
          </Button>
          <LegalAcceptance variant="create" />
          <p className="text-center text-sm text-[#6a6a6a]">
            {t('auth:signup.have_account_prompt')}{' '}
            <Link to="/login" tabIndex={-1} className="text-[#FF6B00] hover:text-[#FF6B00] font-medium" data-testid="signup-login-link">{t('auth:signup.have_account_link')}</Link>
          </p>
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#262626]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-transparent px-2 text-[#444]">{t('auth:signup.or_divider')}</span></div>
          </div>
          {/* Igual que el submit (T5-6): el invitado tampoco se ata al veredicto
              — su propio portero es el que decide.
              AS-5 (D-2): los tres botones de entrada se leen PAREJOS; el invitado venía
              de `text-[#6a6a6a]`.
              REMATE AS-5-TONO: el tono parejo subió otro escalón hacia el blanco,
              `text-[#a0a0a0]` -> `text-[#e5e5e5]` (gemelo del de LoginPage). */}
          <Button type="button" variant="outline" onClick={handleGuest} disabled={loading}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12"
            data-testid="signup-guest-btn">
            <UserCircle className="w-4 h-4 mr-2" /> {t('auth:signup.guest_btn')}
          </Button>
          {/* AS-4 — Apple, WEB-ONLY (el WebView no puede correr el flujo de redirect);
              el nativo va más abajo, gateado al revés.
              AS-5 (D-2): Apple ARRIBA de Google, en los dos mundos. */}
          {!IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={() => irAProveedorWeb('apple')}
            disabled={!!pendienteWeb}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12"
            data-testid="signup-apple-btn">
            {pendienteWeb === 'apple'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>}
            {t('auth:signup.apple_btn')}
          </Button>
          )}
          {/* Google is WEB-ONLY — hidden in app builds (WebView can't run the OAuth
              redirect flow). Native Google lives in the app-only block below. */}
          {!IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={() => irAProveedorWeb('google')}
            disabled={!!pendienteWeb}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12"
            data-testid="signup-google-btn">
            {pendienteWeb === 'google'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
            {t('auth:signup.google_btn')}
          </Button>
          )}
          {/* AS-5 — los mismos dos proveedores por la puerta NATIVA (gate al revés).
              Mismos rótulos, mismos logos, mismo h-12 (48px > 44pt). Con estado de carga
              porque la hoja del sistema no se lleva la página por delante. */}
          {IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={entrarConApple} disabled={!!pendienteSocial}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12"
            data-testid="signup-apple-native-btn">
            {pendienteSocial === 'apple'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>}
            {t('auth:signup.apple_btn')}
          </Button>
          )}
          {IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={entrarConGoogle} disabled={!!pendienteSocial}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12"
            data-testid="signup-google-native-btn">
            {pendienteSocial === 'google'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
            {t('auth:signup.google_btn')}
          </Button>
          )}
        </form>
        )}
        {/* Fuera del condicional a propósito: el widget de Turnstile vive en
            este div y debe SOBREVIVIR al estado "Conectando…" — cada reintento
            de un registro normal pide un token nuevo (getTurnstileToken) y un
            widget desmontado lo dejaría esperando su timeout de 8 s. */}
        <div ref={turnstileRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}
